import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Tabs,
    Tab,
    Paper,
    Alert,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import { AppointmentList } from '../../appointments';
import * as appointmentActions from '../../../actions/user/user.appointments';
import UnifiedPullToRefresh from '../../elements/main/UnifiedPullToRefresh';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`appointments-tabpanel-${index}`}
            aria-labelledby={`appointments-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const UserAppointments = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [tabValue, setTabValue] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [allTabStatusFilter, setAllTabStatusFilter] = useState(''); // Separate filter for "All" tab dropdown
    const [userTimezone, setUserTimezone] = useState(moment.tz.guess());
    
    const {
        userAppointments,
        userAppointmentsLoading,
        userAppointmentsError,
        userAppointmentsPagination,
        timezones,
        actionSuccess
    } = useSelector(state => state.appointments || {});
    
    useEffect(() => {
        // Fetch timezones on component mount
        dispatch(appointmentActions.getTimezones());
        
        // Fetch appointments
        fetchAppointments();
    }, [dispatch, statusFilter, userTimezone, allTabStatusFilter]);
    
    useEffect(() => {
        // Refetch appointments when an action is successful (cancel, etc.)
        if (actionSuccess) {
            fetchAppointments();
            dispatch(appointmentActions.clearAppointmentSuccess());
        }
    }, [actionSuccess, dispatch]);
    
    const fetchAppointments = () => {
        const params = {
            user_timezone: userTimezone
        };
        
        // Use dropdown filter for "All" tab, or tab-based filter for other tabs
        if (tabValue === 0 && allTabStatusFilter) {
            // "All" tab with dropdown filter selected
            params.status = allTabStatusFilter;
        } else if (statusFilter) {
            // Other tabs with tab-based filter
            params.status = statusFilter;
        }
        
        dispatch(appointmentActions.getUserAppointments(params));
    };
    
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        
        // Clear the "All" tab filter when switching to other tabs
        if (newValue !== 1) {
            setAllTabStatusFilter('');
        }
        
        // Update status filter based on tab
        switch (newValue) {
            case 0: // Upcoming
                setStatusFilter('scheduled,confirmed');
                break;
            case 1: // All appointments (excluding cancelled)
                setStatusFilter('');
                break;
            default:
                setStatusFilter('');
        }
    };
    
    const handleBookNewAppointment = () => {
        history.push('/shops'); // Navigate to shops page to select a store
    };
    
    const getUpcomingAppointments = () => {
        return userAppointments.filter(apt => 
            ['scheduled', 'confirmed'].includes(apt.status) &&
            moment(apt.start_time).isAfter(moment())
        );
    };
    
    
    const getFilteredAppointments = () => {
        switch (tabValue) {
            case 0: // Upcoming
                return getUpcomingAppointments();
            default: // All - API already filtered by status
                // API handles filtering, just return the appointments
                return userAppointments;
        }
    };
    
    const handleRefresh = () => {
        // Refresh both timezones and appointments
        dispatch(appointmentActions.getTimezones());
        fetchAppointments();
    };

    return (
        <UnifiedPullToRefresh
            onRefresh={handleRefresh}
            isRefreshing={userAppointmentsLoading}
            refreshText={{
                pull: 'Pull to refresh appointments',
                release: 'Release to refresh appointments',
                refreshing: 'Refreshing appointments...'
            }}
        >
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Typography variant="h6" component="h6">
                    Video Shopping
                    </Typography>
                    
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleBookNewAppointment}
                        size="large"
                    >New Appointment
                    </Button>
                </Box>
            
            {/* Timezone Selector */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Your Timezone</InputLabel>
                        <Select
                            value={userTimezone}
                            onChange={(e) => setUserTimezone(e.target.value)}
                            label="Your Timezone"
                        >
                            {timezones.map((tz) => (
                                <MenuItem key={tz.value} value={tz.value}>
                                    {tz.label} ({tz.offset})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            
            <Paper sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="appointment tabs">
                        <Tab
                            label={`Upcoming (${getUpcomingAppointments().length})`}
                            icon={<ScheduleIcon />}
                            iconPosition="start"
                        />
                        <Tab 
                            label={`All (${userAppointments.length})`} 
                            icon={<ScheduleIcon />} 
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    {getUpcomingAppointments().length === 0 ? (
                        <Box textAlign="center" py={6}>
                            <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                No upcoming appointments
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Book an appointment to see it here.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleBookNewAppointment}
                            >
                                Book Appointment
                            </Button>
                        </Box>
                    ) : (
                        <AppointmentList
                            userType="user"
                            appointments={getUpcomingAppointments()}
                            loading={userAppointmentsLoading}
                            onRefresh={fetchAppointments}
                            hideFilter={true}
                        />
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {userAppointmentsError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {userAppointmentsError}
                        </Alert>
                    ) : null}
                    
                    <AppointmentList
                        userType="user"
                        appointments={getFilteredAppointments()}
                        loading={userAppointmentsLoading}
                        pagination={userAppointmentsPagination}
                        onRefresh={fetchAppointments}
                        hideFilter={false}
                        externalStatusFilter={allTabStatusFilter}
                        onStatusFilterChange={setAllTabStatusFilter}
                    />
                </TabPanel>
                

                
            </Paper>
            
            </Container>
        </UnifiedPullToRefresh>
    );
};

export default UserAppointments;
